// src/components/catalog/ToolCatalog.jsx
//
// The screen almost everyone in the company sees. The backend has already
// filtered `tools` down to what this person's role allows, so everything here
// is about finding one quickly, not about permissions.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, List, SearchX, Star } from 'lucide-react';
import { useTools } from '../../hooks/useTools';
import { useCategories } from '../../hooks/useCategories';
import { useFavorites } from '../../hooks/useFavorites';
import { useDesktopPlatforms } from '../../hooks/useDesktopPlatforms';
import { hasDesktopBuild } from '../../services/desktop';
import { UNCATEGORISED, UNCATEGORISED_KEY, findCategory, isUncategorised } from '../../config/categories';
import ToolIcon from '../common/ToolIcon';
import ToolCard from './ToolCard';
import OpenToolDialog from './OpenToolDialog';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  SearchInput,
  Spinner,
  cx,
} from '../common/ui';

const RECENTS_KEY = 'bib-recent-tools';
const LAYOUT_KEY = 'bib-catalog-layout';

const readStore = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStore = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Convenience only — losing it costs the user nothing important.
  }
};

const ToolCatalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tools, isLoading, error, refetch } = useTools();
  const { categories } = useCategories();
  const { favorites, isFavorite, toggleFavorite } = useFavorites();
  const { platforms } = useDesktopPlatforms();

  const [query, setQuery] = useState('');
  const [layout, setLayout] = useState(() => readStore(LAYOUT_KEY, 'grid'));
  const [recents, setRecents] = useState(() => readStore(RECENTS_KEY, []));
  const [choosing, setChoosing] = useState(null);
  const searchRef = useRef(null);

  const activeCat = searchParams.get('cat') || 'all';

  useEffect(() => writeStore(LAYOUT_KEY, layout), [layout]);

  // ⌘K / Ctrl+K jumps to search from anywhere on the page.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const remember = useCallback((tool) => {
    const next = [tool._id, ...recents.filter((id) => id !== tool._id)].slice(0, 6);
    setRecents(next);
    writeStore(RECENTS_KEY, next);
  }, [recents]);

  const openInBrowser = useCallback((tool) => {
    remember(tool);
    if (/^https?:\/\//i.test(tool.url)) {
      window.open(tool.url, '_blank', 'noopener,noreferrer');
    }
  }, [remember]);

  // A tool with a desktop build asks first; everything else opens straight
  // away, so the common case is still one click.
  const openTool = useCallback((tool) => {
    if (hasDesktopBuild(tool)) {
      remember(tool);
      setChoosing(tool);
      return;
    }
    openInBrowser(tool);
  }, [remember, openInBrowser]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();

    return tools.filter((tool) => {
      if (activeCat === 'fav' && !favorites.includes(tool._id)) return false;
      if (activeCat === UNCATEGORISED_KEY && !isUncategorised(categories, tool)) return false;
      if (!['fav', 'all', UNCATEGORISED_KEY].includes(activeCat) && tool.category !== activeCat) {
        return false;
      }
      if (!q) return true;

      const haystack = [tool.name, tool.description, tool.id, ...(tool.tags || [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [tools, activeCat, favorites, query, categories]);

  // Recently opened is only useful as a shortcut on the unfiltered view.
  const recentTools = useMemo(() => {
    if (activeCat !== 'all' || query.trim()) return [];
    return recents
      .map((id) => tools.find((t) => t._id === id))
      .filter(Boolean)
      .slice(0, 4);
  }, [recents, tools, activeCat, query]);

  if (isLoading) return <Spinner label="Loading your tools…" />;

  if (error) {
    return (
      <ErrorState title="Couldn't load your tools" onRetry={refetch}>
        {error.message}
      </ErrorState>
    );
  }

  const heading =
    activeCat === 'all'
      ? { title: 'All tools', subtitle: 'Everything your role can open, in one place.' }
      : activeCat === 'fav'
      ? { title: 'Favourites', subtitle: "The tools you've starred." }
      : activeCat === UNCATEGORISED_KEY
      ? { title: UNCATEGORISED.label, subtitle: UNCATEGORISED.blurb }
      : {
          title: findCategory(categories, activeCat).label,
          subtitle: findCategory(categories, activeCat).blurb,
        };

  const clearFilters = () => {
    setQuery('');
    setSearchParams({});
  };

  return (
    <div>
      <PageHeader title={heading.title} subtitle={heading.subtitle} />

      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <SearchInput
          ref={searchRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tools by name or what they do…"
          shortcut="⌘K"
          aria-label="Search tools"
          className="min-w-[240px]"
        />

        <div className="flex overflow-hidden rounded-lg border border-line-strong bg-surface">
          {[
            ['grid', LayoutGrid, 'Grid view'],
            ['list', List, 'List view'],
          ].map(([key, Icon, label], i) => (
            <button
              key={key}
              type="button"
              aria-pressed={layout === key}
              aria-label={label}
              title={label}
              onClick={() => setLayout(key)}
              className={cx(
                'grid h-[42px] w-11 place-items-center transition-colors',
                i > 0 && 'border-l border-line',
                layout === key ? 'bg-surface-3 text-ink' : 'text-ink-3 hover:text-ink'
              )}
            >
              <Icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {recentTools.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="mr-1 text-[10.5px] font-semibold uppercase tracking-[0.11em] text-ink-3">
            Recently opened
          </span>
          {recentTools.map((tool) => (
            <button
              key={tool._id}
              type="button"
              onClick={() => openTool(tool)}
              className="inline-flex items-center gap-2 rounded-full border border-line bg-surface py-1.5 pl-2.5 pr-3.5 text-[13px] text-ink-2 transition-colors hover:border-line-strong hover:text-ink"
            >
              <ToolIcon tool={tool} className="h-3.5 w-3.5" />
              {tool.name}
            </button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        query.trim() ? (
          <EmptyState
            icon={SearchX}
            title={`Nothing matches “${query.trim()}”`}
            action={<Button onClick={clearFilters}>Clear search</Button>}
          >
            Try a shorter word, or browse a category from the left.
          </EmptyState>
        ) : activeCat === 'fav' ? (
          <EmptyState icon={Star} title="No favourites yet">
            Star a tool from any list and it'll show up here, ready for tomorrow.
          </EmptyState>
        ) : (
          <EmptyState icon={SearchX} title="Nothing in this category yet">
            {tools.length === 0
              ? 'Your role has no tools assigned. An admin can fix that in Admin → Tools.'
              : 'Tools get added here as they launch.'}
          </EmptyState>
        )
      ) : (
        <div
          className={cx(
            layout === 'grid'
              ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'
              : 'overflow-hidden rounded-card border border-line bg-surface'
          )}
        >
          {visible.map((tool) => (
            <ToolCard
              key={tool._id}
              tool={tool}
              layout={layout}
              category={findCategory(categories, tool.category)}
              isFavorite={isFavorite(tool._id)}
              onToggleFavorite={() => toggleFavorite(tool._id)}
              onOpen={openTool}
            />
          ))}
        </div>
      )}

      {choosing && (
        <OpenToolDialog
          tool={choosing}
          category={findCategory(categories, choosing.category)}
          platforms={platforms}
          onOpenWeb={openInBrowser}
          onClose={() => setChoosing(null)}
        />
      )}
    </div>
  );
};

export default ToolCatalog;
